import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  FileText, Send, CheckCircle2, Receipt, Coins, Hourglass, ShieldCheck,
  Wrench, CheckCheck, Wallet
} from 'lucide-react';

/**
 * Pipeline stages — order MUST mirror backend PIPELINE_ORDER.
 * Each stage maps to a label + colour + icon shown as a small badge or
 * full multi-step pipeline (compact = badge only, full = stepper).
 */
export const PIPELINE_STAGES = [
  { id: 'no_estimate',        label: 'No Estimate',            icon: FileText,     color: 'bg-gray-100 text-gray-500 border-gray-200' },
  { id: 'estimate_draft',     label: 'Estimate Draft',         icon: FileText,     color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { id: 'estimate_sent',      label: 'Estimate Sent',          icon: Send,         color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'estimate_approved',  label: 'Estimate Approved',      icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'invoice_sent',       label: 'Invoice Sent',           icon: Receipt,      color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { id: 'partial_payment',    label: 'Partial Payment',        icon: Coins,        color: 'bg-amber-50 text-amber-800 border-amber-300' },
  { id: 'second_payment',     label: 'Second Payment',         icon: Coins,        color: 'bg-amber-100 text-amber-900 border-amber-400' },
  { id: 'waiting_for_permit', label: 'Waiting for Permit',     icon: Hourglass,    color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { id: 'permit_approved',    label: 'Permit Approved',        icon: ShieldCheck,  color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  { id: 'job_on_schedule',    label: 'Job Scheduled',          icon: Wrench,       color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { id: 'job_completed',      label: 'Job Completed',          icon: CheckCheck,   color: 'bg-teal-50 text-teal-700 border-teal-200' },
  { id: 'invoice_paid',       label: 'Fully Paid',             icon: Wallet,       color: 'bg-green-100 text-green-800 border-green-300' },
];

export function getStage(stageId) {
  return PIPELINE_STAGES.find(s => s.id === stageId) || PIPELINE_STAGES[0];
}

/**
 * Compact one-line badge — used in the projects list.
 */
export default function ProjectPipelineBadge({ stage, history = [], compact = true, size = 'sm' }) {
  const meta = getStage(stage);
  const Icon = meta.icon;

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 ${size === 'xs' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'} rounded-full border font-medium ${meta.color}`}
        data-testid={`pipeline-badge-${stage}`}
      >
        <Icon className={size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        {meta.label}
      </span>
    );
  }

  return (
    <ProjectPipelineStepper currentStage={stage} history={history} />
  );
}

/**
 * Multi-step horizontal pipeline (used inside Project Detail).
 */
export function ProjectPipelineStepper({ currentStage, history = [] }) {
  // We compress the 11 stages into 7 "high-level" milestones for visual clarity.
  const milestones = [
    { id: 'estimate',  label: 'Estimate',        match: ['estimate_draft', 'estimate_sent', 'estimate_approved'], icon: FileText },
    { id: 'approved',  label: 'Approved',        match: ['estimate_approved'],                                    icon: CheckCircle2 },
    { id: 'invoice',   label: 'Invoice',         match: ['invoice_sent', 'partial_payment', 'second_payment'],    icon: Receipt },
    { id: 'permit',    label: 'Permit',          match: ['waiting_for_permit', 'permit_approved'],                icon: ShieldCheck },
    { id: 'job',       label: 'Job',             match: ['job_on_schedule', 'job_completed'],                     icon: Wrench },
    { id: 'paid',      label: 'Paid',            match: ['invoice_paid'],                                          icon: Wallet },
  ];

  const reached = (m) => m.match.some(id => history.includes(id) || id === currentStage);

  return (
    <div className="w-full overflow-x-auto" data-testid="pipeline-stepper">
      <div className="flex items-center min-w-max gap-1">
        {milestones.map((m, idx) => {
          const Icon = m.icon;
          const done = reached(m);
          const isCurrent = m.match.includes(currentStage);
          return (
            <div key={m.id} className="flex items-center">
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${
                  isCurrent
                    ? 'bg-[#003366] text-white border-[#003366] shadow-md ring-2 ring-[#3399FF]/30'
                    : done
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-gray-50 text-gray-400 border-gray-200'
                }`}
                data-testid={`stepper-${m.id}${isCurrent ? '-active' : done ? '-done' : '-pending'}`}
              >
                <Icon className="w-3 h-3" />
                {m.label}
              </div>
              {idx < milestones.length - 1 && (
                <div className={`h-0.5 w-4 ${done ? 'bg-emerald-300' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Hook — fetch pipelines for a list of projects (one batch call).
 * Returns: { [project_id]: { stage, history, ... } }
 */
export function useProjectsPipelinesBatch(deps = []) {
  const [pipelines, setPipelines] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/projects/pipeline/batch')
      .then(res => { if (active) setPipelines(res.data || {}); })
      .catch(() => { if (active) setPipelines({}); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { pipelines, loading };
}

/**
 * Hook — fetch a single project's pipeline.
 */
export function useProjectPipeline(projectId) {
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!projectId) return;
    let active = true;
    setLoading(true);
    api.get(`/projects/${projectId}/pipeline`)
      .then(res => { if (active) setPipeline(res.data); })
      .catch(() => { if (active) setPipeline(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId]);
  return { pipeline, loading };
}
